import type { SupportedLocale } from '../types/simulation'

export interface GuestMessages {
  guestName: string
  guestStatus: string
  guestHint: string
  guestSignIn: string
  guestSessionFailed: string
  onboardingFreeStart: string
}

// Copy for using Voxnela without an account. Registration is optional and only
// adds access to the same images and subscription on other devices.
export const guestMessages: Record<SupportedLocale, GuestMessages> = {
  en: {
    guestName: 'Guest',
    guestStatus: 'Using Voxnela without an account',
    guestHint: 'An account is optional. Sign in only if you want your images and subscription on your other devices too.',
    guestSignIn: 'Sign in or create an account',
    guestSessionFailed: 'Voxnela could not connect. Check your internet connection and try again.',
    onboardingFreeStart: 'Start right away with 3 one-time free AI image generations. No account needed.',
  },
  tr: {
    guestName: 'Misafir',
    guestStatus: 'Voxnela’yı hesapsız kullanıyorsun',
    guestHint: 'Hesap oluşturmak isteğe bağlıdır. Görsellerine ve aboneliğine diğer cihazlarından da ulaşmak istersen giriş yapabilirsin.',
    guestSignIn: 'Giriş yap veya hesap oluştur',
    guestSessionFailed: 'Voxnela bağlantı kuramadı. İnternet bağlantını kontrol edip tekrar dene.',
    onboardingFreeStart: 'Tek seferlik 3 ücretsiz AI görsel üretim hakkınla hemen başla. Hesap gerekmez.',
  },
  de: {
    guestName: 'Gast',
    guestStatus: 'Du nutzt Voxnela ohne Konto',
    guestHint: 'Ein Konto ist optional. Melde dich nur an, wenn du deine Bilder und dein Abo auch auf anderen Geräten nutzen möchtest.',
    guestSignIn: 'Anmelden oder Konto erstellen',
    guestSessionFailed: 'Voxnela konnte keine Verbindung herstellen. Prüfe deine Internetverbindung und versuche es erneut.',
    onboardingFreeStart: 'Starte sofort mit 3 einmaligen kostenlosen KI-Bildern. Kein Konto nötig.',
  },
  es: {
    guestName: 'Invitado',
    guestStatus: 'Usas Voxnela sin cuenta',
    guestHint: 'La cuenta es opcional. Inicia sesión solo si quieres tus imágenes y tu suscripción también en tus otros dispositivos.',
    guestSignIn: 'Iniciar sesión o crear cuenta',
    guestSessionFailed: 'Voxnela no pudo conectarse. Revisa tu conexión a internet y vuelve a intentarlo.',
    onboardingFreeStart: 'Empieza ya con 3 generaciones de imágenes con IA gratis de una sola vez. No necesitas cuenta.',
  },
  fr: {
    guestName: 'Invité',
    guestStatus: 'Vous utilisez Voxnela sans compte',
    guestHint: 'Le compte est facultatif. Connectez-vous seulement si vous voulez retrouver vos images et votre abonnement sur vos autres appareils.',
    guestSignIn: 'Se connecter ou créer un compte',
    guestSessionFailed: 'Voxnela n’a pas pu se connecter. Vérifiez votre connexion internet et réessayez.',
    onboardingFreeStart: 'Commencez tout de suite avec 3 créations d’images IA gratuites et uniques. Aucun compte requis.',
  },
  ru: {
    guestName: 'Гость',
    guestStatus: 'Вы используете Voxnela без аккаунта',
    guestHint: 'Аккаунт необязателен. Войдите, только если хотите видеть свои изображения и подписку и на других устройствах.',
    guestSignIn: 'Войти или создать аккаунт',
    guestSessionFailed: 'Voxnela не удалось подключиться. Проверьте интернет-соединение и повторите попытку.',
    onboardingFreeStart: 'Начните сразу с 3 бесплатных разовых генераций изображений с ИИ. Аккаунт не нужен.',
  },
  it: {
    guestName: 'Ospite',
    guestStatus: 'Stai usando Voxnela senza account',
    guestHint: 'L’account è facoltativo. Accedi solo se vuoi ritrovare immagini e abbonamento anche sugli altri dispositivi.',
    guestSignIn: 'Accedi o crea un account',
    guestSessionFailed: 'Voxnela non è riuscita a connettersi. Controlla la connessione a internet e riprova.',
    onboardingFreeStart: 'Inizia subito con 3 generazioni di immagini IA gratuite una tantum. Nessun account richiesto.',
  },
  pt: {
    guestName: 'Convidado',
    guestStatus: 'Você está usando o Voxnela sem conta',
    guestHint: 'A conta é opcional. Entre apenas se quiser acessar suas imagens e sua assinatura também em outros dispositivos.',
    guestSignIn: 'Entrar ou criar conta',
    guestSessionFailed: 'O Voxnela não conseguiu se conectar. Verifique sua conexão com a internet e tente novamente.',
    onboardingFreeStart: 'Comece agora com 3 gerações de imagens com IA grátis, válidas uma única vez. Não é preciso conta.',
  },
  sv: {
    guestName: 'Gäst',
    guestStatus: 'Du använder Voxnela utan konto',
    guestHint: 'Ett konto är valfritt. Logga bara in om du vill ha dina bilder och din prenumeration på dina andra enheter också.',
    guestSignIn: 'Logga in eller skapa konto',
    guestSessionFailed: 'Voxnela kunde inte ansluta. Kontrollera internetanslutningen och försök igen.',
    onboardingFreeStart: 'Börja direkt med 3 gratis AI-bildgenereringar en gång. Inget konto behövs.',
  },
  nl: {
    guestName: 'Gast',
    guestStatus: 'Je gebruikt Voxnela zonder account',
    guestHint: 'Een account is optioneel. Log alleen in als je je afbeeldingen en abonnement ook op je andere apparaten wilt hebben.',
    guestSignIn: 'Inloggen of account maken',
    guestSessionFailed: 'Voxnela kon geen verbinding maken. Controleer je internetverbinding en probeer het opnieuw.',
    onboardingFreeStart: 'Begin direct met 3 eenmalige gratis AI-afbeeldingen. Geen account nodig.',
  },
  ar: {
    guestName: 'ضيف',
    guestStatus: 'أنت تستخدم Voxnela بدون حساب',
    guestHint: 'إنشاء حساب اختياري. سجّل الدخول فقط إذا أردت الوصول إلى صورك واشتراكك من أجهزتك الأخرى أيضًا.',
    guestSignIn: 'تسجيل الدخول أو إنشاء حساب',
    guestSessionFailed: 'تعذّر على Voxnela الاتصال. تحقّق من اتصالك بالإنترنت وحاول مرة أخرى.',
    onboardingFreeStart: 'ابدأ الآن مع 3 عمليات إنشاء صور مجانية بالذكاء الاصطناعي لمرة واحدة. لا حاجة إلى حساب.',
  },
  hi: {
    guestName: 'अतिथि',
    guestStatus: 'आप बिना खाते के Voxnela इस्तेमाल कर रहे हैं',
    guestHint: 'खाता बनाना वैकल्पिक है। साइन इन तभी करें जब आप अपनी इमेज और सदस्यता अपने दूसरे डिवाइस पर भी चाहते हों।',
    guestSignIn: 'साइन इन करें या खाता बनाएँ',
    guestSessionFailed: 'Voxnela कनेक्ट नहीं हो सका। अपना इंटरनेट कनेक्शन जाँचें और फिर से कोशिश करें।',
    onboardingFreeStart: '3 एक-बार की मुफ़्त AI इमेज के साथ अभी शुरू करें। खाते की ज़रूरत नहीं।',
  },
  id: {
    guestName: 'Tamu',
    guestStatus: 'Anda memakai Voxnela tanpa akun',
    guestHint: 'Akun bersifat opsional. Masuk hanya jika Anda ingin gambar dan langganan Anda juga ada di perangkat lain.',
    guestSignIn: 'Masuk atau buat akun',
    guestSessionFailed: 'Voxnela tidak dapat terhubung. Periksa koneksi internet Anda lalu coba lagi.',
    onboardingFreeStart: 'Mulai sekarang dengan 3 pembuatan gambar AI gratis satu kali. Tanpa perlu akun.',
  },
  ja: {
    guestName: 'ゲスト',
    guestStatus: 'アカウントなしでVoxnelaを利用中',
    guestHint: 'アカウントは任意です。ほかのデバイスでも画像やサブスクリプションを使いたい場合のみサインインしてください。',
    guestSignIn: 'サインインまたはアカウント作成',
    guestSessionFailed: 'Voxnelaに接続できませんでした。インターネット接続を確認して、もう一度お試しください。',
    onboardingFreeStart: '1回限りの無料AI画像生成3回ですぐに始められます。アカウントは不要です。',
  },
  ko: {
    guestName: '게스트',
    guestStatus: '계정 없이 Voxnela를 사용 중입니다',
    guestHint: '계정은 선택 사항입니다. 다른 기기에서도 이미지와 구독을 이용하고 싶을 때만 로그인하세요.',
    guestSignIn: '로그인 또는 계정 만들기',
    guestSessionFailed: 'Voxnela에 연결할 수 없습니다. 인터넷 연결을 확인한 후 다시 시도하세요.',
    onboardingFreeStart: '1회 제공되는 무료 AI 이미지 생성 3회로 바로 시작하세요. 계정이 필요 없습니다.',
  },
  pl: {
    guestName: 'Gość',
    guestStatus: 'Korzystasz z Voxnela bez konta',
    guestHint: 'Konto jest opcjonalne. Zaloguj się tylko, jeśli chcesz mieć swoje obrazy i subskrypcję także na innych urządzeniach.',
    guestSignIn: 'Zaloguj się lub utwórz konto',
    guestSessionFailed: 'Voxnela nie może się połączyć. Sprawdź połączenie z internetem i spróbuj ponownie.',
    onboardingFreeStart: 'Zacznij od razu z 3 jednorazowymi darmowymi generacjami obrazów AI. Konto nie jest potrzebne.',
  },
  th: {
    guestName: 'ผู้เยี่ยมชม',
    guestStatus: 'คุณกำลังใช้ Voxnela โดยไม่มีบัญชี',
    guestHint: 'การสร้างบัญชีเป็นทางเลือก ลงชื่อเข้าใช้เฉพาะเมื่อคุณต้องการใช้รูปภาพและการสมัครสมาชิกบนอุปกรณ์อื่นด้วย',
    guestSignIn: 'ลงชื่อเข้าใช้หรือสร้างบัญชี',
    guestSessionFailed: 'Voxnela เชื่อมต่อไม่ได้ ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตแล้วลองอีกครั้ง',
    onboardingFreeStart: 'เริ่มได้ทันทีด้วยสิทธิ์สร้างภาพ AI ฟรี 3 ครั้งแบบครั้งเดียว ไม่ต้องมีบัญชี',
  },
}
